// Generic slide-over dialog for the v2 shell.
//
// Renders into a portal at document.body. Esc key + backdrop click
// close. Focus is trapped inside while open so keyboard users can't
// tab back into the grid behind. No external library — Radix/Dialog
// would have brought a CSS-reset reconciliation for one component.

import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface SlideOverProps {
  open: boolean;
  onClose(): void;
  ariaLabel: string;
  children: ReactNode;
  /** Width in any CSS length. Default 32rem. */
  width?: string;
}

export function SlideOver({ open, onClose, ariaLabel, children, width = '32rem' }: SlideOverProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const node = panelRef.current;
    // Defer one tick so the portal mounts before we ask for focusables.
    const t = window.setTimeout(() => {
      const first = node?.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }, 0);

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !node) return;
      const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.hasAttribute('disabled')
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      window.clearTimeout(t);
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-[2px] transition-opacity"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        // Mobile: full-width sheet (the side panel itself is narrow,
        // a centred 32rem panel would overflow). md+: anchored right
        // at the configured width.
        className="absolute right-0 top-0 h-full w-full surface border-l border-divider shadow-2xl flex flex-col animate-slide-in"
        style={{ maxWidth: width }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
